import { useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useDropzone } from 'react-dropzone';
import {
  Zap, User, Mail, Lock, Eye, EyeOff, Phone, FileText,
  CheckCircle2, XCircle, Loader2, AlertTriangle, Upload,
  Trash2, Send, Shield, Camera, MapPin, DollarSign,
} from 'lucide-react';
import { Button, Input, Card, CardBody } from '../components/ui';
import { PasswordStrengthIndicator } from '../components/security/PasswordStrengthIndicator';
import {
  useValidateInvite, useUseInviteLink, useDocumentTypes,
  useMyDocuments, useUploadDocument, useSubmitDocuments,
} from '../hooks/useInviteLinks';
import { useAuthStore } from '../stores/auth.store';
import { api } from '../lib/api';
import { clearPasswordResetFlag } from '../lib/passwordResetFlag';

const registerSchema = z.object({
  name: z.string().min(3, 'Nome deve ter no mínimo 3 caracteres'),
  email: z.string().email('Email inválido'),
  password: z
    .string()
    .min(8, 'Senha deve ter no mínimo 8 caracteres')
    .regex(/[A-Z]/, 'Senha deve ter ao menos uma letra maiúscula')
    .regex(/[a-z]/, 'Senha deve ter ao menos uma letra minúscula')
    .regex(/\d/, 'Senha deve ter ao menos um número'),
  confirmPassword: z.string(),
  cpfCnpj: z.string().min(11, 'CPF/CNPJ é obrigatório').refine(
    (val) => {
      const digits = val.replace(/\D/g, '');
      return digits.length === 11 || digits.length === 14;
    },
    { message: 'CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos' }
  ),
  phone: z.string().optional(),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'As senhas não conferem',
  path: ['confirmPassword'],
});

type RegisterForm = z.infer<typeof registerSchema>;

type Step = 'register' | 'documents' | 'complete';

const DOC_ICONS: Record<string, React.ElementType> = {
  // Fallback codes
  ID_FRONT: FileText,
  ID_BACK: FileText,
  SELFIE: Camera,
  ADDRESS_PROOF: MapPin,
  INCOME_PROOF: DollarSign,
  // Tenant-configured codes
  ID_DOCUMENT: FileText,
  SELFIE_WITH_ID: Camera,
  PROOF_OF_RESIDENCE: MapPin,
  PROOF_OF_INCOME: DollarSign,
};

export function InviteRegister() {
  const { token } = useParams<{ token: string }>();
  const [step, setStep] = useState<Step>('register');
  const [showPassword, setShowPassword] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const { data: invite, isLoading: validating, error: validationError } = useValidateInvite(token || '');
  const useMutation = useUseInviteLink();

  const { register, handleSubmit, watch, formState: { errors } } = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
  });
  const passwordValue = watch('password') ?? '';

  const onSubmit = async (data: RegisterForm) => {
    if (!token) return;
    try {
      const result = await useMutation.mutateAsync({
        token,
        data: {
          name: data.name,
          email: data.email,
          password: data.password,
          cpfCnpj: data.cpfCnpj,
          phone: data.phone || undefined,
        },
      });

      // Auto-login: set token in api and auth store
      if (result.accessToken) {
        localStorage.setItem('accessToken', result.accessToken);
        localStorage.setItem('refreshToken', result.refreshToken);
        api.defaults.headers.common['Authorization'] = `Bearer ${result.accessToken}`;
        // Brand new account — no pending password reset
        clearPasswordResetFlag();
        setIsAuthenticated(true);
      }

      setStep('documents');
    } catch {
      // Error handled by mutation
    }
  };

  const errorMessage = (validationError as any)?.response?.data?.message
    || (useMutation.error as any)?.response?.data?.message
    || '';

  return (
    <div className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-accent-500/10 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg relative z-10"
      >
        {/* Logo */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 mb-4 shadow-glow">
            <Zap className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold gradient-text">
            {import.meta.env.VITE_APP_NAME || 'Hub Business'}
          </h1>
        </div>

        {/* Steps indicator */}
        {step !== 'register' && (
          <div className="flex items-center justify-center gap-2 mb-6">
            {['register', 'documents', 'complete'].map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  step === s ? 'bg-primary-500 text-white' :
                  ['documents', 'complete'].indexOf(step) > i - 1 ? 'bg-green-500 text-white' :
                  'bg-surface-700 text-surface-400'
                }`}>
                  {['documents', 'complete'].indexOf(step) > i - 1 && step !== s ? '✓' : i + 1}
                </div>
                {i < 2 && <div className="w-8 h-0.5 bg-surface-700" />}
              </div>
            ))}
          </div>
        )}

        <Card variant="glass" className="backdrop-blur-xl">
          <CardBody className="p-8">
            {/* Loading */}
            {validating && (
              <div className="text-center py-8">
                <Loader2 className="w-10 h-10 animate-spin text-primary-400 mx-auto mb-4" />
                <p className="text-surface-400">Validando convite...</p>
              </div>
            )}

            {/* Invalid */}
            {!validating && (validationError || !invite?.valid) && (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-8 h-8 text-red-400" />
                </div>
                <h2 className="text-xl font-bold text-surface-100 mb-2">Link Inválido</h2>
                <p className="text-surface-400 text-sm mb-6">
                  {errorMessage || 'Este link de convite não é válido, já foi utilizado ou expirou.'}
                </p>
                <Link to="/login"><Button variant="primary">Ir para Login</Button></Link>
              </div>
            )}

            {/* Step 1: Register */}
            {!validating && invite?.valid && step === 'register' && (
              <RegisterStep
                invite={invite}
                errors={errors}
                register={register}
                handleSubmit={handleSubmit}
                onSubmit={onSubmit}
                showPassword={showPassword}
                setShowPassword={setShowPassword}
                isPending={useMutation.isPending}
                mutationError={useMutation.error}
                passwordValue={passwordValue}
              />
            )}

            {/* Step 2: Documents */}
            {step === 'documents' && isAuthenticated && (
              <DocumentsStep onComplete={() => setStep('complete')} tenantId={invite?.tenantId} />
            )}

            {/* Step 3: Complete */}
            {step === 'complete' && (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-green-400" />
                </div>
                <h2 className="text-xl font-bold text-surface-100 mb-2">Tudo Pronto!</h2>
                <p className="text-surface-400 text-sm mb-2">
                  Sua conta foi criada e seus documentos foram enviados para análise.
                </p>
                <p className="text-surface-500 text-xs mb-6">
                  O administrador irá revisar seus documentos e ativar sua conta em breve.
                </p>
                <Link to="/login">
                  <Button variant="primary" className="w-full">Ir para Login</Button>
                </Link>
              </div>
            )}
          </CardBody>
        </Card>
      </motion.div>
    </div>
  );
}

// ===== Register Step Component =====
function RegisterStep({
  invite, errors, register, handleSubmit, onSubmit,
  showPassword, setShowPassword, isPending, mutationError,
  passwordValue,
}: any) {
  return (
    <>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-surface-100">Criar sua Conta</h2>
        {invite.personName ? (
          <p className="text-surface-400 mt-1">
            Olá <span className="text-primary-400 font-medium">{invite.personName}</span>, você foi convidado(a) para <span className="text-primary-400 font-medium">{invite.tenantName}</span>
          </p>
        ) : (
          <p className="text-surface-400 mt-1">
            Você foi convidado para <span className="text-primary-400 font-medium">{invite.tenantName}</span>
          </p>
        )}
      </div>

      {mutationError && (
        <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {(mutationError as any)?.response?.data?.message || 'Erro ao criar conta'}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input label="Nome Completo" placeholder={invite?.accountType === 'PJ' ? 'Razão social da empresa' : 'Seu nome completo'} leftIcon={<User className="w-4 h-4" />} error={errors.name?.message} {...register('name')} />
        <Input label="Email" type="email" placeholder="seu@email.com" leftIcon={<Mail className="w-4 h-4" />} error={errors.email?.message} {...register('email')} />
        <Input label={invite?.accountType === 'PJ' ? 'CNPJ' : 'CPF'} placeholder={invite?.accountType === 'PJ' ? '00.000.000/0000-00' : '000.000.000-00'} leftIcon={<FileText className="w-4 h-4" />} error={errors.cpfCnpj?.message} {...register('cpfCnpj')} />
        <Input label="Telefone (opcional)" placeholder="(00) 00000-0000" leftIcon={<Phone className="w-4 h-4" />} error={errors.phone?.message} {...register('phone')} />
        <div className="space-y-2">
          <Input
            label="Senha" type={showPassword ? 'text' : 'password'} placeholder="Mínimo 8 caracteres"
            leftIcon={<Lock className="w-4 h-4" />}
            rightIcon={
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-surface-500 hover:text-surface-300">
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            }
            error={errors.password?.message} {...register('password')}
          />
          <PasswordStrengthIndicator password={passwordValue || ''} />
        </div>
        <Input
          label="Confirmar Senha" type={showPassword ? 'text' : 'password'} placeholder="Repita a senha"
          leftIcon={<Lock className="w-4 h-4" />}
          error={errors.confirmPassword?.message} {...register('confirmPassword')}
        />
        <Button type="submit" variant="primary" className="w-full" isLoading={isPending}>
          Criar Conta e Enviar Documentos
        </Button>
      </form>

      <div className="mt-4 text-center">
        <Link to="/login" className="text-sm text-primary-400 hover:text-primary-300">
          Já tem uma conta? Faça login
        </Link>
      </div>
    </>
  );
}

// ===== Documents Step Component =====
function DocumentsStep({ onComplete, tenantId }: { onComplete: () => void; tenantId?: string }) {
  const { data: docTypes, isLoading: loadingTypes } = useDocumentTypes(tenantId);
  const { data: myDocs, isLoading: loadingDocs } = useMyDocuments();
  const uploadMutation = useUploadDocument();
  const submitMutation = useSubmitDocuments();
  const [uploadingType, setUploadingType] = useState<string | null>(null);

  const getUploadedDoc = (code: string) => myDocs?.find(d => d.documentType === code);

  const requiredTypes = docTypes?.filter(dt => dt.isRequired) || [];
  const uploadedRequired = requiredTypes.filter(dt => getUploadedDoc(dt.code));
  const canSubmit = requiredTypes.length > 0 && uploadedRequired.length === requiredTypes.length;
  const progress = requiredTypes.length > 0 ? Math.round((uploadedRequired.length / requiredTypes.length) * 100) : 0;

  const handleUpload = useCallback(async (file: File, documentType: string) => {
    setUploadingType(documentType);
    try {
      await uploadMutation.mutateAsync({ file, documentType });
    } finally {
      setUploadingType(null);
    }
  }, [uploadMutation]);

  const handleSubmit = async () => {
    try {
      await submitMutation.mutateAsync();
      onComplete();
    } catch {
      // Error handled by mutation
    }
  };

  if (loadingTypes || loadingDocs) {
    return (
      <div className="text-center py-8">
        <Loader2 className="w-8 h-8 animate-spin text-primary-400 mx-auto mb-4" />
        <p className="text-surface-400">Carregando...</p>
      </div>
    );
  }

  return (
    <>
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="w-6 h-6 text-primary-400" />
          <h2 className="text-xl font-bold text-surface-100">Envie seus Documentos</h2>
        </div>
        <p className="text-surface-400 text-sm">
          Para ativar sua conta, envie os documentos abaixo para verificação.
        </p>
      </div>

      {/* Progress */}
      <div className="mb-6">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-surface-400">{uploadedRequired.length} de {requiredTypes.length} obrigatórios</span>
          <span className={progress === 100 ? 'text-green-400 font-bold' : 'text-surface-400'}>{progress}%</span>
        </div>
        <div className="h-2 bg-surface-700 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-500 rounded-full ${progress === 100 ? 'bg-green-500' : 'bg-primary-500'}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Document cards */}
      <div className="space-y-3 mb-6">
        {docTypes?.map((dt) => (
          <DocUploadCard
            key={dt.code}
            docType={dt}
            uploadedDoc={getUploadedDoc(dt.code)}
            isUploading={uploadingType === dt.code}
            onUpload={(file) => handleUpload(file, dt.code)}
          />
        ))}
      </div>

      {/* Error */}
      {(uploadMutation.error || submitMutation.error) && (
        <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {(uploadMutation.error as any)?.response?.data?.message ||
           (submitMutation.error as any)?.response?.data?.message ||
           'Erro ao processar'}
        </div>
      )}

      {/* Submit */}
      <Button
        variant="primary"
        className="w-full"
        onClick={handleSubmit}
        disabled={!canSubmit}
        isLoading={submitMutation.isPending}
      >
        <Send className="w-4 h-4 mr-2" />
        Enviar Documentos para Análise
      </Button>

      {!canSubmit && (
        <p className="text-xs text-surface-500 text-center mt-2">
          Envie todos os documentos obrigatórios para continuar
        </p>
      )}
    </>
  );
}

// ===== Document Upload Card =====
function DocUploadCard({
  docType, uploadedDoc, isUploading, onUpload,
}: {
  docType: { code: string; name: string; description: string; isRequired: boolean; maxFileSizeMb: number; allowedMimeTypes: string[] };
  uploadedDoc?: { id: string; originalName: string; fileSize: number; status: string; createdAt: string };
  isUploading: boolean;
  onUpload: (file: File) => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const Icon = DOC_ICONS[docType.code] || FileText;

  const onDrop = useCallback((accepted: File[]) => {
    setError(null);
    if (accepted.length > 0) {
      const file = accepted[0];
      const sizeMb = file.size / (1024 * 1024);
      if (sizeMb > docType.maxFileSizeMb) {
        setError(`Arquivo muito grande (${sizeMb.toFixed(1)}MB). Máximo: ${docType.maxFileSizeMb}MB`);
        return;
      }
      onUpload(file);
    }
  }, [onUpload, docType.maxFileSizeMb]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: docType.allowedMimeTypes.reduce((acc: Record<string, string[]>, t: string) => { acc[t] = []; return acc; }, {}),
    maxSize: docType.maxFileSizeMb * 1024 * 1024,
    multiple: false,
    disabled: isUploading,
  });

  const formatSize = (bytes: number) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="rounded-xl border border-surface-700 bg-surface-800/50 p-4">
      <div className="flex items-start gap-3 mb-2">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${uploadedDoc ? 'bg-green-500/20' : 'bg-surface-700'}`}>
          {uploadedDoc ? <CheckCircle2 className="w-5 h-5 text-green-400" /> : <Icon className="w-5 h-5 text-surface-400" />}
        </div>
        <div className="flex-1">
          <h4 className="text-sm font-medium text-surface-100 flex items-center gap-2">
            {docType.name}
            {docType.isRequired && <span className="text-xs text-red-400">*</span>}
          </h4>
          <p className="text-xs text-surface-500">{docType.description}</p>
        </div>
      </div>

      {error && (
        <div className="mb-2 text-xs text-red-400 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" /> {error}
        </div>
      )}

      {uploadedDoc ? (
        <div className="flex items-center gap-2 p-2 bg-green-500/10 border border-green-500/20 rounded-lg">
          <FileText className="w-4 h-4 text-green-400" />
          <span className="text-xs text-surface-300 flex-1 truncate">{uploadedDoc.originalName}</span>
          <span className="text-xs text-surface-500">{formatSize(uploadedDoc.fileSize)}</span>
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={`border border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
            isDragActive ? 'border-primary-500 bg-primary-500/10' : 'border-surface-600 hover:border-surface-500'
          }`}
        >
          <input {...getInputProps()} />
          {isUploading ? (
            <div className="flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-primary-400" />
              <span className="text-xs text-surface-400">Enviando...</span>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2">
              <Upload className="w-4 h-4 text-surface-500" />
              <span className="text-xs text-surface-400">
                {isDragActive ? 'Solte aqui' : 'Clique ou arraste o arquivo'}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default InviteRegister;
